import { GetCommand, QueryCommand, DeleteCommand } from '@aws-sdk/lib-dynamodb';
import { config } from './config';
import { getDdb } from './aws';
import { SharedVideo } from './types';

// Matches PublishFn's SHARED_LIBRARY_BUCKET env var — the constant GSI partition key that lets
// every signed-in user Query (not Scan) the whole catalog in addedAt order.
const SHARED_LIBRARY_BUCKET = 'ALL';

/**
 * Fetches the complete shared catalog in one call (looping the underlying Query via
 * LastEvaluatedKey). Sort, search, and grid/list view all need the full set in memory to behave
 * correctly, and at this project's catalog scale (hundreds, low thousands) that's cheap — see
 * design.md's "full-catalog fetch" decision in the improve-library-ux change.
 */
export async function listSharedLibrary(): Promise<SharedVideo[]> {
  const items: SharedVideo[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  do {
    const r = await getDdb().send(
      new QueryCommand({
        TableName: config.sharedLibraryTable,
        IndexName: config.sharedLibraryByTimeIndex,
        // `bucket` is a DynamoDB reserved keyword — must be aliased via ExpressionAttributeNames
        // or the query fails with "Invalid KeyConditionExpression: Attribute name is a reserved
        // keyword".
        KeyConditionExpression: '#bucket = :b',
        ExpressionAttributeNames: { '#bucket': 'bucket' },
        ExpressionAttributeValues: { ':b': SHARED_LIBRARY_BUCKET },
        ScanIndexForward: false,
        Limit: 500,
        ExclusiveStartKey: exclusiveStartKey,
      }),
    );
    items.push(...((r.Items ?? []) as SharedVideo[]));
    exclusiveStartKey = r.LastEvaluatedKey as Record<string, unknown> | undefined;
  } while (exclusiveStartKey);
  return items;
}

export async function getSharedVideo(videoId: string): Promise<SharedVideo | null> {
  const r = await getDdb().send(
    new GetCommand({ TableName: config.sharedLibraryTable, Key: { videoId } }),
  );
  return (r.Item as SharedVideo | undefined) ?? null;
}

/**
 * Any signed-in user may unshare any item — a deliberate product decision, not an oversight.
 * It's also the only way to ever remove the backfilled videos, which have no real owner in this
 * app. No Lambda needed: the authenticated role has a direct DeleteItem grant on this table (see
 * identity-stack.ts's grantSharedLibraryAccess) precisely because there's no ownership check to
 * enforce here, unlike publishing.
 */
export async function unshareVideo(videoId: string): Promise<void> {
  await getDdb().send(
    new DeleteCommand({ TableName: config.sharedLibraryTable, Key: { videoId } }),
  );
}
